// Constants
import { FLAG_PAGINATION_COUNT } from "@benchmark/core/constants";

// Hooks
import { useCallback, useEffect, useState, startTransition } from "react";
import { useMedplum } from "@medplum/react";

// Types
import type { Flag } from "@medplum/fhirtypes";

// Resolvers
import { isFlagWithinActivePeriod } from "@benchmark/core/resolvers";

// Events
import { DomainEventNames } from "@benchmark/core/events";
import { useEventDrivenRefetch } from "@benchmark/core/hooks";

/**
 * Fetches Flag resources that are currently in force for a patient.
 *
 * `status=active` alone is not enough: nothing flips a Flag to `inactive`
 * when its `period` elapses, so an expired pause would keep rendering a
 * "Paused" chip long after the pause ended. Flags outside their period are
 * dropped here so every consumer sees the same answer as the canonical
 * pause resolvers.
 * @agentic @benchmark/core
 */
export function usePatientFlags(patientId?: string) {
  const medplum = useMedplum();
  const [flags, setFlags] = useState<Flag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!patientId) {
      startTransition(() => {
        setFlags([]);
      });
      return;
    }

    let cancelled = false;
    startTransition(() => {
      setLoading(true);
      setError(null);
    });

    medplum
      .search("Flag", {
        patient: patientId,
        status: "active",
        _count: FLAG_PAGINATION_COUNT,
      })
      .then((bundle) => {
        if (cancelled) return;

        const list: Flag[] = (bundle.entry || []).flatMap((e) =>
          e.resource &&
          e.resource.resourceType === "Flag" &&
          isFlagWithinActivePeriod(e.resource as Flag)
            ? [e.resource as Flag]
            : [],
        );
        startTransition(() => {
          setFlags(list);
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching flags:", err);
        startTransition(() => {
          setError(err);
          setFlags([]);
        });
      })
      .finally(() => {
        if (!cancelled) {
          startTransition(() => {
            setLoading(false);
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [patientId, medplum, reloadKey]);

  // Auto-refetch on flag events for this patient by bumping the reload key.
  const triggerReload = useCallback(() => setReloadKey((k) => k + 1), []);
  useEventDrivenRefetch(
    [DomainEventNames.FlagCreated, DomainEventNames.FlagUpdated],
    {
      refetch: triggerReload,
      enabled: !!patientId,
      filter: (payload) =>
        (payload as { patientId?: string }).patientId === patientId,
    },
  );

  return { flags, loading, error };
}
